import {
  getFullUrl,
  getStep,
  trackDollCustomOption,
  trackFlowCompleted,
  trackFlowStarted,
  trackModal,
  trackStepCompleted,
  trackStepStarted,
} from '@/utils/subflowAnalyticsHelpers';
import { SUBFLOW_STEPS_V3, SUBSCRIPTION_EVENT_LABELS } from '@/core/constants';
import { PaymentOption } from './usePaymentOptions';
import { SubscriptionAddOn } from './useAddOns';
import { pushToDataLayer } from '@/utils/dataLayer';
import { usePathname } from '@/navigation';
import {
  SubFlowV3ContextData,
  useSubFlowV3Data,
} from '@/components/subflow-v3/context/SubFlowV3Context';

type StepNameTypes =
  | string
  | 'Child Info'
  | 'Starting Kit'
  | 'Payment Options'
  | 'Add-ons'
  | 'Order Review';

export enum PaymentOptionEnum {
  PAYGO = 'Paygo',
  PUF = 'PUF',
  RECURRING_PUF = 'Recurring PUF',
}

export interface StepCompletion {
  stepName?: StepNameTypes | null;
  stepNumber?: number | null;
  paymentOption: PaymentOptionEnum | null;
  bundleSize: number | null;
  childNameAdded: boolean | null;
  childBirthDate: any;
  startingKitName: string | null;
  booksAdded: boolean | null;
  previousPlayAdded: boolean | null;
  standaloneAdded: boolean | null;
  url: string | null;
  isLastStep?: boolean;
  lastStepName?: string;
  lastStepNum?: number;
  selectedCustomOption?: string;
  bookName?: string;
}

export function useStepTracking(version = 'v3') {
  const path = usePathname() || '';
  const { data } = useSubFlowV3Data();

  const { name: stepName, stepNum } = getStep(path) || {};

  const isSubflowV3 = version === 'v3';

  const getDOB = (data: SubFlowV3ContextData) => {
    const { year, month, day } = data.childInfo ?? {};
    return year && month && day ? `${year}-${month}-${day}` : null;
  };

  const getEventPayload = (data: SubFlowV3ContextData) => {
    const hasPreviousPlay = data?.previousPlayAddOns.length > 0;
    const hasStandAloneItems = data?.standaloneAddOns.length > 0;

    const hasBooksAddOn = (
      subscriptionAddOns: SubscriptionAddOn[] | undefined,
    ) => {
      if (!subscriptionAddOns) return false;

      const addOnItem = subscriptionAddOns.find(
        (addOn) => addOn.kind == 'books_addon',
      );
      return addOnItem ? true : false;
    };

    const getBookName = (
      subscriptionAddOns: SubscriptionAddOn[] | undefined,
    ) => {
      if (!subscriptionAddOns || isSubflowV3) return '';

      const addOnItens = subscriptionAddOns.filter(
        (addOn) => addOn.kind == 'books_addon',
      );

      if (!addOnItens.length) return '';

      return addOnItens[0].components[0].anchor;
    };

    const getPaymentOptionType = (paymentOption: PaymentOption | undefined) => {
      if (!paymentOption) return null;

      const { size } = paymentOption;

      return size === 1 ? PaymentOptionEnum.PAYGO : PaymentOptionEnum.PUF;
    };

    const values: StepCompletion = {
      stepName: stepName || ' Child Info',
      stepNumber: stepNum || 1,
      paymentOption: getPaymentOptionType(data?.paymentOption),
      bundleSize: data?.paymentOption?.size || null,
      childNameAdded: data?.childInfo?.name ? true : false,
      childBirthDate: getDOB(data),
      startingKitName: data?.startingKit?.name || null,
      booksAdded: hasBooksAddOn(data?.subscriptionAddOns),
      previousPlayAdded: hasPreviousPlay,
      standaloneAdded: hasStandAloneItems,
      url: getFullUrl(path),
      selectedCustomOption: data.customizationId,
      bookName: getBookName(data?.subscriptionAddOns),
    };

    return values;
  };

  const onStepViewed = (data: SubFlowV3ContextData) => {
    const values = getEventPayload(data);
    trackStepStarted(values);
    pushToDataLayer({ event: 'sub_pur_flow', step: values.stepName });
  };

  const onStepComplete = async (data: SubFlowV3ContextData) => {
    const values = getEventPayload(data);

    const steps = SUBFLOW_STEPS_V3;

    values.isLastStep = stepNum === steps.length;
    trackStepCompleted(values);

    if (values.stepNumber === 1 && values.selectedCustomOption) {
      pushToDataLayer({
        event: 'sub_pur_custom',
        sub_custom_option: values.selectedCustomOption,
      });
    }

    if (values.stepNumber === 4 && values.booksAdded) {
      pushToDataLayer({
        event: 'sub_pur_book_add',
        item_name: values.bookName,
      });
    }
  };

  const onFlowStart = () => {
    const step = getStep(path);
    if (step && step.stepNum === 1) {
      trackFlowStarted(path, version);
    }
  };

  const onModalOpen = (
    data: SubFlowV3ContextData,
    startingKitInfo: SubFlowV3ContextData['startingKit'],
  ) => {
    const eventPayload = {
      childBirthDate: getDOB(data),
      startingKitName: startingKitInfo?.name || '',
      url: getFullUrl(path),
    };

    trackModal(
      SUBSCRIPTION_EVENT_LABELS.RECOMMENDED_PLAY_KIT_MODAL_VIEWED,
      eventPayload,
    );
  };

  const onModalClose = (
    data: SubFlowV3ContextData,
    startingKitInfo: SubFlowV3ContextData['startingKit'],
  ) => {
    const eventPayload = {
      childBirthDate: getDOB(data),
      startingKitName: startingKitInfo?.name || '',
      url: getFullUrl(path),
    };

    trackModal(
      SUBSCRIPTION_EVENT_LABELS.RECOMMENDED_PLAY_KIT_MODAL_DISMISSED,
      eventPayload,
    );
  };

  const onSelectedCustomOption = (
    data: SubFlowV3ContextData,
    selectedCustomOptions: string,
  ) => {
    const eventPayload = {
      selectedCustomOptions,
      startingKitName: data?.startingKit?.name || '',
    };

    trackDollCustomOption(
      SUBSCRIPTION_EVENT_LABELS.SELECTED_CUSTOM_OPTION,
      eventPayload,
    );
  };

  const onFlowComplete = () => {
    if (!data) return;

    const values = getEventPayload(data);
    delete values.stepName; // we don't want this value for flow complete
    delete values.stepNumber; // we don't want this value for flow complete
    values.lastStepName = stepName;
    values.lastStepNum = stepNum;

    trackFlowCompleted(values);
  };

  return {
    onStepViewed,
    onStepComplete,
    onFlowComplete,
    onFlowStart,
    onModalOpen,
    onModalClose,
    onSelectedCustomOption,
  };
}
